<template>
  <div style="background: var(--bg-page-gradient); font-family: var(--font-sans); color: var(--ink-700)">
    <hr class="vdb-dash" style="margin: 0 var(--vdb-pad-x)" />
    <section style="padding: 80px var(--vdb-pad-x)">

      <!-- Header -->
      <div style="text-align: center; max-width: 820px; margin: 0 auto 56px">
        <div class="vdb-eyebrow" style="margin-bottom: 16px; justify-content: center; display: inline-flex">
          <span class="dot"></span> Join the library
        </div>
        <h2 style="margin: 0; font-weight: 500; font-size: 56px; letter-spacing: -0.025em; line-height: 1.0; color: var(--ink-800)">
          Take your <em style="font-family: var(--font-editorial); font-style: italic; font-weight: 500">research</em><br/>
          to the next level.
        </h2>
        <p style="margin-top: 20px; font-size: 16px; color: var(--ink-400)">
          <em class="vdb-italic" style="color: var(--ink-800)">{{ weeklyCount || '184' }}</em> new videos tagged this week ·
          <em class="vdb-italic" style="color: var(--ink-800)">{{ formatNumber(totalLibrary) }}</em> in the library ·
          updated <em class="vdb-italic" style="color: var(--ink-800)">daily</em>.
        </p>
      </div>

      <!-- 3-column grid -->
      <div class="cta-hero-grid">
        <!-- Free sidecar -->
        <div class="cta-sidecar">
          <span class="cta-eyebrow-chip" style="background: var(--cream-200); color: var(--ink-700)">Free</span>
          <h3 class="cta-sidecar-name">Free</h3>
          <div class="cta-sidecar-tagline">The open library.</div>
          <div class="cta-price-strip cta-price-strip--paper">
            <span class="cta-price-amount cta-price-amount--sidecar">$0</span>
            <span style="font-size: 12px; color: var(--ink-400)">forever</span>
          </div>
          <p class="cta-sidecar-blurb">Browse, search, save. Full library access with filters.</p>
          <div class="cta-feature-list" style="margin-bottom: 22px; flex: 1">
            <div v-for="f in basicFeatures" :key="f" class="cta-feature-row cta-feature-row--sidecar">
              <span style="color: var(--ink-500); flex-shrink: 0; margin-top: 2px"><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg></span>
              <span>{{ f }}</span>
            </div>
          </div>
          <router-link to="/signup" humblytics="pricing-cta-basic" class="vdb-btn-pill ghost cta-sidecar-cta">
            Get started free <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
          </router-link>
        </div>

        <!-- Pro hero -->
        <div class="cta-hero-card">
          <!-- Popular badge -->
          <div class="cta-hero-badge">
            <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l2 7 7 2-7 2-2 7-2-7-7-2 7-2z"/></svg>
            Popular
          </div>

          <div>
              <div style="font-size: 11px; font-weight: 600; letter-spacing: 0.08em; text-transform: uppercase; color: rgba(247,240,235,0.6); margin-bottom: 12px">Most picked</div>
              <h3 class="cta-hero-title">Pro</h3>
              <div class="cta-hero-tagline">Study faster. Post more.</div>
          </div>

          <!-- Fanned thumbnails -->
          <div class="cta-hero-thumbs">
            <div style="position: absolute; top: 0; left: 0; width: 90px; aspect-ratio: 9/13; border-radius: 8px; border: 2px solid var(--ink-800); transform: rotate(-5deg); box-shadow: 0 4px 12px rgba(0,0,0,0.4); z-index: 0; overflow: hidden; background: linear-gradient(135deg, #3a3632 0%, #2a2725 100%)">
              <img src="https://obchfdkehwfbicjxlxpu.supabase.co/storage/v1/object/public/video-thumbnails/analyzer/instagram/instagram_DPDshpFAgoc.jpg" alt="" style="width:100%;height:100%;object-fit:cover;display:block" />
            </div>
            <div style="position: absolute; top: 0; left: 56px; width: 90px; aspect-ratio: 9/13; border-radius: 8px; border: 2px solid var(--ink-800); transform: rotate(4deg); box-shadow: 0 4px 12px rgba(0,0,0,0.4); z-index: 1; overflow: hidden; background: linear-gradient(135deg, #4a4440 0%, #332f2c 100%)">
              <img src="https://obchfdkehwfbicjxlxpu.supabase.co/storage/v1/object/public/video-thumbnails/analyzer/instagram/instagram_DSNcOCqDS12.jpg" alt="" style="width:100%;height:100%;object-fit:cover;display:block" />
            </div>
          </div>

          <!-- Price strip -->
          <div class="cta-price-strip cta-price-strip--hero">
            <span class="cta-price-amount cta-price-amount--hero">$79</span>
            <span style="font-size: 14px; color: rgba(247,240,235,0.65)">/month</span>
          </div>

          <!-- Body: features + quote -->
          <div class="cta-hero-body">
            <div>
              <div v-for="(f, i) in creatorFeatures" :key="f.label" class="cta-feature-row cta-feature-row--hero" :style="i > 0 ? { borderTop: '1px dashed rgba(247,240,235,0.12)' } : {}">
                <span style="flex-shrink: 0; margin-top: 2px" :style="{ color: f.signature ? 'var(--success-500)' : 'var(--cream-100)' }">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                </span>
                <span :style="{ fontFamily: f.italic ? 'var(--font-display-italic)' : 'var(--font-sans)', fontStyle: f.italic ? 'italic' : 'normal' }">{{ f.label }}</span>
              </div>
            </div>
            <div style="display: flex; align-items: center;">
              <div class="cta-hero-stat-callout">
                <div style="margin-bottom: 14px;">
                  <span style="font-family: var(--font-display-italic); font-style: italic; font-size: 36px; line-height: 1; color: var(--cream-100);">{{ formatNumber(dailyVideoCount || 173) }}</span>
                  <span style="font-size: 13px; color: rgba(247,240,235,0.65); margin-left: 6px;">videos added<br/>in the last 24 hours</span>
                </div>
                <div style="border-top: 1px dashed rgba(247,240,235,0.15); padding-top: 10px; font-size: 13px; color: rgba(247,240,235,0.55); line-height: 1.45;">
                  from <em class="vdb-italic" style="color: var(--cream-100);">{{ formatNumber(monitoredAccounts) }}</em> curated accounts
                </div>
              </div>
            </div>
          </div>

          <!-- CTA -->
          <div>
            <router-link :to="isAuthenticated ? '/subscription' : '/signup?tier=creator'" humblytics="pricing-cta-creator" class="cta-hero-cta-btn">
              {{ isAuthenticated ? 'Upgrade to Pro' : 'Start free trial' }}
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
            </router-link>
            <div style="margin-top: 12px; text-align: center; font-size: 12px; color: rgba(247,240,235,0.55)">
              <em class="vdb-italic" style="color: var(--cream-100)">7-day free trial</em> · cancel anytime
            </div>
          </div>
        </div>

        <!-- Strategist sidecar -->
        <div class="cta-sidecar">
          <span class="cta-eyebrow-chip" style="background: rgba(216,86,0,0.10); color: var(--warm-600)">Coming soon</span>
          <h3 class="cta-sidecar-name">Strategist</h3>
          <div class="cta-sidecar-tagline">For teams who study at scale.</div>
          <div class="cta-price-strip cta-price-strip--paper">
            <span class="cta-price-amount cta-price-amount--sidecar">$99</span>
            <span style="font-size: 12px; color: var(--ink-400)">/month</span>
          </div>
          <p class="cta-sidecar-blurb">Multiple voice profiles, account tracking, CSV export, and more.</p>
          <div class="cta-feature-list" style="margin-bottom: 22px; flex: 1">
            <div v-for="f in strategistFeatures" :key="f" class="cta-feature-row cta-feature-row--sidecar">
              <span style="color: var(--ink-500); flex-shrink: 0; margin-top: 2px"><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg></span>
              <span>{{ f }}</span>
            </div>
          </div>
          <div>
            <router-link to="/waitlist?plan=strategist" humblytics="pricing-cta-strategist" class="vdb-btn-pill ghost cta-sidecar-cta">
              Coming soon
            </router-link>
            <div v-if="memberCount >= 50" style="margin-top: 10px; text-align: center; font-size: 11px; color: var(--ink-400)">
              <em class="vdb-italic" style="color: var(--ink-800)">{{ formatNumber(memberCount) }}</em> creators and strategists already on the platform
            </div>
          </div>
        </div>
      </div>

    </section>
    <hr class="vdb-dash" style="margin: 0 var(--vdb-pad-x)" />
  </div>
</template>

<script>
import api from '@/services/api.js';
import { videoApi } from '@/services/api.js';
import { authManager } from '@/services/auth.js';

export default {
  name: 'PricingCTAHero',
  data() {
    return {
      weeklyCount: null,
      totalLibrary: 47312,
      memberCount: 0,
      monitoredAccounts: 0,
      dailyVideoCount: null,
    };
  },
  computed: {
    isAuthenticated() {
      return authManager.isAuthenticated || !!localStorage.getItem('access_token');
    },
    basicFeatures() {
      return [
        'Full video library',
        'All filters & taxonomies',
        'Hook templates',
        'Deep analysis on every video',
        'Saves',
      ];
    },
    creatorFeatures() {
      return [
        { label: 'Saved voice profile' },
        { label: 'Idea & hook generation', signature: true },
        { label: 'VideoDNA.md' },
        { label: 'Build & share swipefiles' },
        { label: 'Follow creators & formats' },
        { label: 'Submit videos for analysis' },
        { label: 'Agentic strategy chatbot' },
        { label: 'VideoDatabase MCP Beta' },
      ];
    },
    strategistFeatures() {
      return [
        'Manage multiple voice profiles',
        'Add new accounts to track',
        'CSV export',
      ];
    },
  },
  async mounted() {
    this.fetchStats();
    this.fetchDailyUpdate();
  },
  methods: {
    formatNumber(n) {
      if (!n) return '0';
      return n.toLocaleString();
    },
    async fetchStats() {
      try {
        const [weeklyRes, statsRes] = await Promise.all([
          api.get('/stats/weekly-videos'),
          api.get('/stats'),
        ]);
        if (weeklyRes.data.success) {
          this.weeklyCount = weeklyRes.data.count;
        }
        if (statsRes.data.success) {
          this.totalLibrary = statsRes.data.data.videosAnalyzed || this.totalLibrary;
          this.monitoredAccounts = statsRes.data.data.monitoredAccounts || 0;
          this.memberCount = Number(statsRes.data.data.userCount) || 0;
        }
      } catch (e) {
        // silent — fallback values already set
      }
    },
    async fetchDailyUpdate() {
      try {
        const response = await videoApi.get('/updates', {
          params: { update_type: 'daily', limit: 1, offset: 0 }
        });
        if (response.data?.data?.[0]?.video_count) {
          this.dailyVideoCount = response.data.data[0].video_count;
        }
      } catch (e) {
        // silent — fallback used
      }
    },
  },
};
</script>

<style scoped>
.cta-hero-grid {
  display: grid;
  grid-template-columns: 1fr 1.5fr 1fr;
  gap: 18px;
  max-width: 1240px;
  margin: 0 auto;
  align-items: stretch;
}

.cta-sidecar {
  background: var(--paper);
  border: 1px solid var(--line-100);
  border-radius: 18px;
  padding: 24px 22px 22px;
  display: flex;
  flex-direction: column;
  box-shadow: var(--shadow-card-sm);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.cta-sidecar:hover {
  transform: translateY(-1px);
  box-shadow: var(--shadow-card-md);
}
.cta-eyebrow-chip {
  align-self: flex-start;
  padding: 3px 9px;
  margin-bottom: 14px;
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  border-radius: 100px;
}
.cta-sidecar-name {
  margin: 0 0 4px;
  font-family: var(--font-sans);
  font-weight: 500;
  font-size: 30px;
  letter-spacing: -0.02em;
  line-height: 1.05;
  color: var(--ink-800);
}
.cta-sidecar-tagline {
  font-family: var(--font-display-italic);
  font-style: italic;
  font-size: 15px;
  color: var(--ink-500);
  margin-bottom: 18px;
}
.cta-sidecar-blurb {
  margin: 0 0 18px;
  font-size: 13.5px;
  color: var(--ink-500);
  line-height: 1.5;
}
.cta-sidecar-cta {
  width: 100%;
  justify-content: center;
  background: transparent;
  color: var(--ink-800);
  border: 1.5px solid var(--ink-800);
  text-decoration: none;
  box-sizing: border-box;
}

.cta-price-strip {
  display: flex;
  align-items: baseline;
  gap: 6px;
  padding: 12px 0;
  margin-bottom: 18px;
}
.cta-price-strip--paper {
  border-top: 1px dashed var(--cream-500);
  border-bottom: 1px dashed var(--cream-500);
}
.cta-price-strip--hero {
  gap: 10px;
  padding: 14px 0;
  border-top: 1px dashed rgba(247,240,235,0.20);
  border-bottom: 1px dashed rgba(247,240,235,0.20);
}
.cta-price-amount {
  font-family: var(--font-display-italic);
  font-style: italic;
  font-weight: 500;
  color: var(--ink-800);
  line-height: 1;
}
.cta-price-amount--sidecar {
  font-size: 34px;
}
.cta-price-amount--hero {
  font-family: var(--font-display-italic);
  font-style: italic;
  font-size: 56px;
  line-height: 1;
  color: var(--cream-100);
  font-weight: 500;
}

.cta-feature-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 5px 0;
}
.cta-feature-row--sidecar {
  font-size: 12.5px;
  color: var(--ink-700);
}
.cta-feature-row--hero {
  gap: 10px;
  padding: 6px 0;
  font-size: 13px;
  color: var(--cream-100);
}

.cta-hero-card {
  position: relative;
  background: var(--ink-800);
  color: var(--cream-100);
  border-radius: 22px;
  padding: 28px 28px 26px;
  box-shadow: var(--shadow-lift);
  display: grid;
  grid-template-rows: auto auto auto auto;
  transition: box-shadow 0.2s ease;
}
.cta-hero-card:hover .cta-hero-cta-btn {
  filter: brightness(1.04);
}
.cta-hero-badge {
  position: absolute;
  top: 18px;
  right: 18px;
  padding: 5px 11px;
  background: var(--cream-100);
  color: var(--ink-800);
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  border-radius: 100px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.cta-hero-body {
  display: grid;
  grid-template-columns: 1.05fr 1fr;
  gap: 22px;
  margin-bottom: 22px;
}
.cta-hero-stat-callout {
  padding: 16px 18px;
  background: rgba(247,240,235,0.06);
  border: 1px dashed rgba(247,240,235,0.15);
  border-radius: 12px;
  width: 100%;
  box-sizing: border-box;
}
.cta-hero-cta-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px 22px;
  background: var(--cream-100);
  color: var(--ink-800);
  border-radius: 100px;
  text-decoration: none;
  font-size: 16px;
  font-weight: 500;
  border: none;
  cursor: pointer;
  width: 100%;
  box-sizing: border-box;
  font-family: var(--font-sans);
  transition: filter 0.15s ease;
}
.cta-hero-cta-btn:hover {
  filter: brightness(1.08);
}
.cta-hero-cta-btn:active {
  transform: scale(0.98);
}

.cta-hero-title {
  margin: 0 0 6px;
  font-weight: 500;
  font-size: 52px;
  letter-spacing: -0.025em;
  line-height: 1;
  color: var(--cream-100);
}
.cta-hero-tagline {
  font-family: var(--font-display-italic);
  font-style: italic;
  font-size: 22px;
  color: rgba(247,240,235,0.8);
  margin-bottom: 18px;
}
.cta-hero-thumbs {
  position: absolute;
  right: -10px;
  top: 80px;
  width: 180px;
  height: 140px;
  z-index: 3;
}

@media (max-width: 1099px) {
  .cta-hero-grid {
    grid-template-columns: 1fr;
    max-width: 600px;
  }
  .cta-hero-card {
    order: -1;
  }
  .cta-hero-body {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 819px) {
  .cta-hero-grid {
    max-width: 480px;
  }
}

@media (max-width: 768px) {
  .cta-hero-card {
    padding: 22px 20px 20px;
  }
  .cta-hero-title {
    font-size: 38px;
  }
  .cta-hero-tagline {
    font-size: 18px;
    margin-bottom: 12px;
  }
  .cta-hero-thumbs {
    top: 60px;
    right: -6px;
    width: 130px;
    height: 110px;
  }
  .cta-hero-thumbs > div {
    width: 70px !important;
  }
  .cta-hero-thumbs > div:nth-child(2) {
    left: 42px !important;
  }
  .cta-hero-body {
    grid-template-columns: 1fr;
    gap: 14px;
    margin-bottom: 16px;
  }
  .cta-hero-stat-callout {
    padding: 12px 14px;
  }
  .cta-feature-row--hero {
    padding: 4px 0;
    font-size: 12.5px;
  }
  .cta-price-amount--hero {
    font-size: 40px;
  }
  .cta-price-strip--hero {
    padding: 10px 0;
    margin-bottom: 14px;
  }
  .cta-hero-cta-btn {
    padding: 12px 18px;
    font-size: 15px;
  }
}
</style>
